"use client";

import clsx from "clsx";
import { useTranslations } from "next-intl";
import type { ReconciliationState } from "@/lib/sse/useSSE";

/**
 * Screen 2.3 — Trade Docs Reconciliation. A 6×6 matrix where rows and
 * columns are the six poultry documents; cells are the cross-document
 * field checks (quantity, HS code, consignee LEI, gross weight, container
 * ID, port of loading/discharge, vessel IMO, shipping marks).
 *
 * The audience watches cells flip green as matches resolve. The
 * "auto-resolved" beat highlights the WeightToleranceRule for the
 * Packing List ↔ Commercial Invoice cell — 27,998 kg vs 28,000 kg sits
 * within ±0.1% tolerance, flashes amber for ~600 ms, then resolves green.
 */

const DOCS = [
  { type: "CommercialInvoice",         short: "Inv" },
  { type: "HalalSlaughterCertificate", short: "Halal" },
  { type: "VeterinaryHealthCertificate", short: "Vet" },
  { type: "CertificateOfOrigin",       short: "CoO" },
  { type: "PackingList",               short: "Pack" },
  { type: "EBL",                       short: "eBL" },
] as const;

const FIELD_KEYS = ["fieldQuantity", "fieldHsCode", "fieldConsigneeLei", "fieldGrossWeight", "fieldContainer", "fieldPorts", "fieldVesselImo", "fieldShippingMarks"] as const;

export function ReconciliationMatrix({
  reconciliation,
  showEblDescError = false,
  showAwbDescError = false,
  className,
}: {
  reconciliation: ReconciliationState;
  /** When true, surface the eBL ↔ Commercial Invoice product-description
   *  LinguisticEquivalenceRule auto-resolve banner AND flash the matching
   *  matrix cell amber → green. T2 (4.7) sets this; T1 (2.3) keeps only
   *  the weight-tolerance banner. */
  showEblDescError?: boolean;
  /** When true, surface the AWB ↔ Commercial Invoice piece-count
   *  TolerancePieceCountRule auto-resolve banner AND flash the matching
   *  matrix cell amber → green. T4 (7.7) sets this; the AWB occupies
   *  the carrier-doc row position (same as eBL on T1/T2). */
  showAwbDescError?: boolean;
  className?: string;
}) {
  const t = useTranslations("reconciliationMatrix");
  const completed = reconciliation.status === "completed";
  const autoResolved = reconciliation.weightToleranceAutoResolved;

  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="flex items-end justify-between">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("title")}</p>
          <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{t("subtitle")}</h3>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px] text-orbitra-teal-soft">
          {completed ? (
            <span className="rounded-full border border-policy-pass/40 bg-policy-pass/10 px-3 py-1 text-policy-pass">
              {t("consistencyComplete")}
            </span>
          ) : (
            <span className="rounded-full border border-state-traced/40 bg-state-traced/10 px-3 py-1 text-state-traced">
              {t("consistencyEvaluating")}
            </span>
          )}
          {reconciliation.reportId && (
            <span className="rounded-full border border-state-traced/40 px-3 py-1 text-state-traced">
              {t("reportSuffix", { id: reconciliation.reportId })}
            </span>
          )}
        </div>
      </div>

      <div className="grid flex-1 grid-cols-7 gap-1 font-mono text-[10px]">
        <span />
        {DOCS.map((d, di) => (
          <span key={d.short} className="px-2 py-1 text-center text-orbitra-teal-soft">
            {di === 5 && showAwbDescError ? "AWB" : d.short}
          </span>
        ))}
        {DOCS.map((row, ri) => (
          <Row key={row.type} short={ri === 5 && showAwbDescError ? "AWB" : row.short} ri={ri} completed={completed} autoResolved={autoResolved} showEblDescError={showEblDescError} showAwbDescError={showAwbDescError} />
        ))}
      </div>

      {autoResolved && (
        <div className="glass flex items-start gap-3 px-4 py-3 text-[11px]">
          <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-state-financed">
            {t("weightChip")}
          </span>
          <p className="text-orbitra-paper">
            {t("weightBody", { pack: "27,998 kg", inv: "28,000 kg" })}
          </p>
        </div>
      )}

      {/* Second auto-resolved flag — eBL product description mismatch with
       *  the Commercial Invoice. Only shown on T2 (4.7) via the
       *  showEblDescError prop; T1 (2.3) deliberately keeps a single
       *  reconciliation beat (the weight-tolerance one). Gated on
       *  `completed` rather than `autoResolved` so the banner fires
       *  independently of the weight-tolerance beat — T2's scenario
       *  doesn't carry a weight-tolerance event. */}
      {completed && showEblDescError && (
        <div className="glass flex items-start gap-3 px-4 py-3 text-[11px]">
          <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-state-financed">
            {t("eblChip")}
          </span>
          <p className="text-orbitra-paper">
            {t("eblBody", { eblDesc: "\"FROZEN HALAL WHOLE CHICKEN\"", invDesc: "\"Frozen Halal whole chicken\"" })}
            <span className="ms-2 inline-flex items-center gap-1 rounded-full border border-policy-pass/40 bg-policy-pass/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-policy-pass">
              {t("resolvedChip")}
            </span>
          </p>
        </div>
      )}

      {/* T4-specific auto-resolved flag — Air Waybill ↔ Commercial Invoice
       *  carton-count delta. Mirrors the eBL flag shape but covers the
       *  air-freight piece-count tolerance (chilled-meat ULD repalletisation
       *  at Qatar Airways Cargo's Doha hub). */}
      {completed && showAwbDescError && (
        <div className="glass flex items-start gap-3 px-4 py-3 text-[11px]">
          <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-state-financed">
            {t("awbChip")}
          </span>
          <p className="text-orbitra-paper">
            {t("awbBody", { awb: "48 cartons", inv: "47 cartons" })}
            <span className="ms-2 inline-flex items-center gap-1 rounded-full border border-policy-pass/40 bg-policy-pass/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-policy-pass">
              {t("resolvedChip")}
            </span>
          </p>
        </div>
      )}

      <div className="grid grid-cols-4 gap-2 text-[9px] text-orbitra-muted">
        {FIELD_KEYS.map((k) => (
          <span key={k} className="rounded-md border border-state-traced/15 bg-orbitra-ink/40 px-2 py-1">
            {t("fieldPrefix")} {t(k)}
          </span>
        ))}
      </div>
    </div>
  );
}

function Row({ short, ri, completed, autoResolved, showEblDescError, showAwbDescError }: { short: string; ri: number; completed: boolean; autoResolved: boolean; showEblDescError: boolean; showAwbDescError: boolean }) {
  return (
    <>
      <span className="flex items-center justify-end pe-2 text-orbitra-teal-soft">{short}</span>
      {[0, 1, 2, 3, 4, 5].map((ci) => {
        const onDiagonal = ri === ci;
        // Highlight the Packing List ↔ Commercial Invoice weight cell when
        // we've seen the WeightToleranceRule auto-resolve (rows are in DOCS
        // order: 0=Inv, 4=Pack).
        const isWeightCell = (ri === 0 && ci === 4) || (ri === 4 && ci === 0);
        // Carrier doc ↔ Commercial Invoice auto-resolve cell (0=Inv,
        // 5=carrier doc). eBL = T2 4.7 (LinguisticEquivalenceRule);
        // AWB = T4 7.7 (TolerancePieceCountRule). Same matrix coordinate.
        const isCarrierDescCell = (showEblDescError || showAwbDescError) && ((ri === 0 && ci === 5) || (ri === 5 && ci === 0));
        const cellState = onDiagonal
          ? "self"
          : (isWeightCell && autoResolved) || (isCarrierDescCell && completed)
            ? "auto"
            : completed
              ? "matched"
              : "pending";
        return (
          <Cell key={ci} state={cellState} isWeightCell={isWeightCell} isEblDescCell={showEblDescError && isCarrierDescCell} isAwbDescCell={showAwbDescError && isCarrierDescCell} />
        );
      })}
    </>
  );
}

function Cell({ state, isWeightCell, isEblDescCell, isAwbDescCell }: { state: "self" | "auto" | "matched" | "pending"; isWeightCell: boolean; isEblDescCell: boolean; isAwbDescCell: boolean }) {
  const t = useTranslations("reconciliationMatrix");
  if (state === "self") {
    return <span className="rounded border border-state-traced/15 bg-orbitra-ink/40" />;
  }
  const tooltip =
    isWeightCell  ? t("tooltipWeight") :
    isEblDescCell ? t("tooltipEblDesc") :
    isAwbDescCell ? t("tooltipAwbDesc") :
                    undefined;
  return (
    <span
      title={tooltip}
      className={clsx(
        "flex items-center justify-center rounded border text-[10px] transition-colors duration-state ease-state",
        state === "matched" && "border-policy-pass/40 bg-policy-pass/15 text-policy-pass",
        state === "auto"    && "border-state-financed/50 bg-state-financed/15 text-state-financed shadow-[0_0_12px_var(--state-financed-glow)]",
        state === "pending" && "border-state-future/30 bg-state-future/5 text-state-future/80",
      )}
    >
      {state === "matched" ? "✓" : state === "auto" ? "≈" : "·"}
    </span>
  );
}
